import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, AlertCircle, Loader2, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../../stores/authStore';
import { AuthImagePanel } from '../../components/AuthImagePanel/AuthImagePanel';

/**
 * Reset password page — user lands here from the magic-link email.
 * Supabase automatically handles the token exchange from the URL hash.
 */
export default function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const { updatePassword, loading, error, clearError } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (password.length < 8) {
      useAuthStore.setState({ error: 'Password must be at least 8 characters.' });
      return;
    }
    if (password !== confirm) {
      useAuthStore.setState({ error: 'Passwords do not match.' });
      return;
    }

    const res = await updatePassword(password);
    if (res.success) {
      setDone(true);
      setTimeout(() => navigate('/admin/login', { replace: true }), 3000);
    }
  };

  return (
    <div className="min-h-screen flex bg-ui-background">
      <AuthImagePanel />

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <Link to="/" className="text-3xl font-extrabold text-brand-dark italic tracking-tight">
              smothers
            </Link>
            <h1 className="text-2xl font-bold text-brand-dark mt-4">Set new password</h1>
            <p className="text-sm text-brand-gray mt-1">
              Choose a new password for your account.
            </p>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-start gap-3 p-3 mb-5 bg-red-50 border border-error/20 rounded-xl"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle size={18} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{error}</p>
            </motion.div>
          )}

          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-success" />
              </div>
              <h2 className="text-lg font-bold text-brand-dark mb-2">Password updated!</h2>
              <p className="text-sm text-brand-gray">Redirecting to sign in...</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New password */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-brand-dark mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ui-muted" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className="w-full pl-11 pr-11 py-3 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-all placeholder:text-ui-muted"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ui-muted hover:text-brand-dark transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm */}
              <div>
                <label htmlFor="confirm" className="block text-sm font-semibold text-brand-dark mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ui-muted" />
                  <input
                    id="confirm"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Confirm your new password"
                    className="w-full pl-11 pr-4 py-3 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-all placeholder:text-ui-muted"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={clsx(
                  'w-full flex items-center justify-center gap-2 py-3 text-sm font-bold rounded-xl transition-all',
                  loading
                    ? 'bg-brand-red/60 text-brand-white cursor-not-allowed'
                    : 'bg-brand-red hover:bg-red-700 text-brand-white shadow-lg hover:shadow-xl'
                )}
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          )}

          <p className="mt-8 text-center text-xs text-brand-gray">
            <Link to="/admin/login" className="text-brand-red hover:text-red-700 font-medium">
              &larr; Back to Sign In
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
